import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext } from 'react'
import useAxios from '../hooks/useAxios'
import { AxiosError, AxiosInstance } from 'axios'
import { Text } from '@fluentui/react'
import ErrorPopover from '../components/Toast/ErrorPopover'
import { normalizeAxiosError } from '../reducers/errorReducer'
import AuthorizationContext, { Host } from './AuthorizationContext'
import useAppToast, { AppToast, TOAST_TIMEOUT_NEVER } from '../hooks/useAppToast'

export type MolieState = {
    code?: string
    state?: string
    error?: string
    errorDescription?: string
}

export type MollieContextType = {
    authorize: (location: Location) => void
}

const doAuthorize = (axios: AxiosInstance, host: Host, location: Location, toast: AppToast) => {
    //skip when there are no succes or failure parameters
    if (location.hash?.indexOf('/mollie') < 0) {
        return
    }
    if (location.hash?.indexOf('success') >= 0) {
        const values = location.hash.split('/')

        toast.promise(
            axios.get<string>(`/payment/mollie/authorization/${host.RowKey}?code=${values[3]}&state=${values[4]}&hostKey=${host.RowKey}`, {
                baseURL: process.env.API_HOST,
                headers: { SuppressErrors: true },
            }),
            {
                pending: 'Bezig met instellen van de Mollie koppeling',
                success: () => 'De Mollie koppeling is succesvol geautoriseerd',
                error: (reason: AxiosError) => (
                    <>
                        <Text>Er is een fout opgetreden bij het autoriseren van de Mollie koppeling.</Text>
                        <ErrorPopover error={normalizeAxiosError(reason)} />
                    </>
                ),
            },
        )
    }

    if (location.hash?.indexOf('error') >= 0) {
        const values = location.hash.split('/')

        toast.showMessage(
            <>
                <Text>Er is een fout opgetreden bij het autoriseren van de Mollie koppeling.</Text>
                <Text>Foutmelding: {values[3]}</Text>
                <Text>Details: {values[4]}</Text>
            </>,
            { intent: 'error', timeout: TOAST_TIMEOUT_NEVER },
        )
    }
}

const MollieContext = createContext<MollieContextType>(null)

export function MollieProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const toast = useAppToast()

    return (
        <MollieContext.Provider
            value={{
                authorize: (location: Location) => doAuthorize(axios, currentHost, location, toast),
            }}
        >
            {props.children}
        </MollieContext.Provider>
    )
}

export default MollieContext
